import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Success page specifically for iframe redirects from payment gateway
 * This page posts a message to the parent window and should not be navigated to directly
 * Renders an empty/invisible page to avoid flashing content in the iframe
 */
const PaymentIframeSuccess = () => {
    const location = useLocation();
    const hasPostedMessage = useRef(false);

    useEffect(() => {
        // Only post message once
        if (hasPostedMessage.current) {
            console.log('Already posted success message, skipping');
            return;
        }
        
        // Get payment token from URL if available
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        // Extract any transaction details from the URL
        const transactionId = params.get('transaction_id') || params.get('order_id') || 'unknown';

        // Mark as posted to prevent duplicate messages
        hasPostedMessage.current = true;
        
        // Post message to parent window immediately
        window.parent.postMessage({
            status: 'successful',
            transaction_id: transactionId,
            token: token
        }, '*');

        console.log('Posted success message to parent window');

        // After a brief delay, send another message as a safeguard
        const timeoutId = setTimeout(() => {
            window.parent.postMessage({
                status: 'successful',
                transaction_id: transactionId,
                token: token,
                retry: true
            }, '*');
            console.log('Posted backup success message to parent window');
        }, 300); // Reduced delay to make transition faster
        
        // Set a max lifetime for this component
        const maxLifetimeTimeout = setTimeout(() => {
            if (window.parent !== window) {
                // Try to close this window/iframe if possible
                try {
                    window.parent.postMessage({
                        status: 'successful',
                        transaction_id: transactionId,
                        token: token,
                        final: true
                    }, '*');
                } catch (e) {
                    console.error('Error posting final message:', e);
                }
            }
        }, 2000); // Max 2 seconds lifetime
        
        return () => {
            clearTimeout(timeoutId);
            clearTimeout(maxLifetimeTimeout);
        };
    }, [location]);

    // Return an empty/invisible page
    return null;
};

export default PaymentIframeSuccess; 